// Feedback Button Component
// ==========================
// Floating feedback button for collecting user feedback during beta

import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';

interface FeedbackButtonProps {
  context?: string;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ context = 'general' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!feedback.trim() && rating === null) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'anonymous',
        context,
        rating,
        feedback: feedback.trim(),
        url: window.location.pathname,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setFeedback('');
        setRating(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
    setIsSubmitting(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-all"
        aria-label="Giv feedback"
      >
        Feedback
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 w-80 border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">Giv feedback</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Luk"
        >
          x
        </button>
      </div>

      {submitted ? (
        <div className="text-center py-4">
          <span className="text-4xl" role="img" aria-label="Celebration">!</span>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Tak for din feedback!</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Hvordan er din oplevelse?</p>

          <div className="flex justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className={`text-2xl hover:scale-110 transition-transform ${
                  rating && rating >= star ? 'opacity-100' : 'opacity-30'
                }`}
                aria-label={`${star} stjerner`}
              >
                *
              </button>
            ))}
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Fortæl os hvad du synes... (valgfrit)"
            className="w-full p-2 border rounded-lg resize-none h-24 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (!feedback.trim() && rating === null)}
            className="w-full mt-3"
          >
            {isSubmitting ? 'Sender...' : 'Send feedback'}
          </Button>
        </>
      )}
    </div>
  );
};

export default FeedbackButton;
